import {
    ClmmPositionExpanded,
    LedgerDebt,
    LoanPnlStats,
    LoanStatus,
    PastLoanCollateral,
    TokenListMetadata,
    TokenListMetadataExpanded
} from "@bridgesplit/abf-sdk";

import { Replace } from "../reducers";
import type { ApiUserLoansResponse, LoanMatrixEventApi } from "./api";
import { Ledger, LoanCollateral, LoanInfo, LoansAggregate } from "./domain/loan";
import type {
    ParsedLoanEvent,
    ParsedBorrowPrincipalMetadata,
    ParsedRepayPrincipalMetadata,
    ParsedRefinanceLedgerMetadata,
    ParsedSellLedgerMetadata,
    ParsedLiquidateLedgerMetadata
} from "./domain/events";

export * from "@bridgesplit/abf-sdk";

export type UserLoansResponse = ApiUserLoansResponse;

/** Per-loan LTV breakdown for the visible page. All ratios are fractional
 *  (0.85 = 85%); `healthRatio` is `1 − ltv / liquidationThreshold`, clamped to
 *  `[0, 1]` (1 = healthy, 0 = at-LQT). */
export interface LoanRowHealth {
    loanAddress: string;
    /** Current LTV (oracle-uncertainty-biased worst case). */
    ltv: number;
    /** Strategy's max LTV, USD-weighted across the loan's collateral positions. */
    strategyLtv: number;
    /** Liquidation threshold, USD-weighted. Strictly greater than `strategyLtv`. */
    liquidationThreshold: number;
    /** Liquidation health ratio in `[0, 1]`. Derived from `ltv` /
     *  `liquidationThreshold`; kept on the wire for non-breaking rollout. */
    healthRatio: number;
}

/** Loop group (`POST /user/loans` with `orderFundingTypes = Loop`), keyed by
 *  collateral asset. Same shape as the legacy `UserLoansGroup` plus new
 *  loop-specific fields: pre-summed native position size, all-time pnl %,
 *  and per-loan healths for the visible page. */
export interface UserLoopGroup {
    collateralMint: string;
    collateralIdentifier: string;
    /** Sum of collateral amount across the group, in `collateralMint`'s
     *  native base units (decimals not applied). Decoded from the wire's
     *  decimal-string representation. **Precision note:** values above
     *  `Number.MAX_SAFE_INTEGER` (2^53−1) lose precision; matches the
     *  convention used elsewhere in the codebase for native amounts. */
    netPositionTokenAmount: number;
    /** All-time PnL fraction: `aggregate.pnlUsd / Σ netInflowUsd`. */
    pnlPctChange: number;
    aggregate: LoansAggregate;
    wAvgHealth: number;
    wAvgNetApy: number;
    /** Page-scoped — only populated for groups on the visible page. */
    rowHealths: LoanRowHealth[];
    collateralMetadata: TokenListMetadata;
}

/** Non-loop group (`POST /user/loans` with `orderFundingTypes ≠ Loop`),
 *  keyed by principal mint. No `wAvgHealth` / `wAvgNetApy` — those don't
 *  apply outside the loop weighting. Use `rowHealths` for per-loan health
 *  on the visible page. */
export interface UserLoanGroup {
    principalMint: string;
    /** Sum of outstanding principal in `principalMint`'s native base units. */
    borrowedTokenAmount: number;
    aggregate: LoansAggregate;
    /** Page-scoped — only populated for groups on the visible page. */
    rowHealths: LoanRowHealth[];
    principalMetadata: TokenListMetadata;
}

export type AbfLoanExpanded = Replace<
    LoanInfo,
    {
        pnlStats: LoanPnlStats | null;
        loanLoopVaultPairIdentifier?: string | undefined;
        netApyLive?: number | undefined;
        index: number;
        status: LoanStatus;
        borrower: string;
        lenders: string[]; //List of strategy addresses
        borrowerWAvgApy: number;
        principalMetadata: TokenListMetadata;
        principalUsdPrice: number | undefined;
        // Off-chain market price for the principal mint; debt is valued at this on the live chart point.
        principalMarketPrice: number | undefined;

        pastCollateral: PastLoanCollateral[];
        //Api Replace
        ledger: LedgerExpanded[]; //The live ledger
        pastLedgers: LedgerExpanded[]; //These are ledgers that have been closed
        collateral: LoanCollateralExpanded[];
        events: ParsedLoanEventExpanded[];
    }
>;

export type LedgerExpanded = Replace<
    Ledger,
    {
        ledgerDebt: LedgerDebt;
        isFilled: boolean;
    }
>;

export type LoanCollateralExpanded = Replace<
    LoanCollateral,
    {
        metadata: TokenListMetadataExpanded;
        usdPrice: number | undefined;
        uncertainty: number | undefined;
        oracleAccount: string | undefined;
        clmmPosition?: ClmmPositionExpanded;
    }
>;

export type ParsedLoanEventExpanded = Replace<
    ParsedLoanEvent,
    {
        eventAssetMetadata: TokenListMetadata | undefined;
        matrixSnapshots: LoanMatrixEventApi[];
        action: DerivedLoanEventActionType;
        actionMetadata: DerivedLoanEventActionMetadata;
    }
>;

export enum DerivedLoanEventActionType {
    AddCollateral = "addCollateral",
    RemoveCollateral = "removeCollateral",
    BorrowPrincipal = "borrowPrincipal",
    RepayPrincipal = "repayPrincipal",
    RefinanceLedger = "refinanceLedger",
    SellLedger = "sellLedger",
    Liquidate = "liquidate",
    // Derived FE types
    FullLiquidation = "fullLiquidation",
    PartialLiquidation = "partialLiquidation"
}

export type DerivedLoanEventActionMetadata =
    | { type: DerivedLoanEventActionType.AddCollateral; params: null }
    | { type: DerivedLoanEventActionType.RemoveCollateral; params: null }
    | { type: DerivedLoanEventActionType.BorrowPrincipal; params: ParsedBorrowPrincipalMetadata }
    | { type: DerivedLoanEventActionType.RepayPrincipal; params: ParsedRepayPrincipalMetadata }
    | { type: DerivedLoanEventActionType.RefinanceLedger; params: ParsedRefinanceLedgerMetadata }
    | { type: DerivedLoanEventActionType.SellLedger; params: ParsedSellLedgerMetadata }
    | { type: DerivedLoanEventActionType.Liquidate; params: ParsedLiquidateLedgerMetadata }
    // Derived FE types
    | { type: DerivedLoanEventActionType.FullLiquidation; params: DerivedLiquidationEventActionMetadata }
    | { type: DerivedLoanEventActionType.PartialLiquidation; params: DerivedLiquidationEventActionMetadata };

export type DerivedLiquidationEventActionMetadata = ParsedLiquidateLedgerMetadata & {
    repaymentEventMetadata: ParsedRepayPrincipalMetadata;
};
